import { useId, useState, type FormEvent } from 'react';
import { useAuth } from '../lib/auth';
import { ApiRequestError } from '../lib/api';

export function SignIn({
  onStarted,
  onFailed,
}: {
  /** Told the moment credentials are submitted, so the doors can wait. */
  onStarted?: () => void;
  /** Told when the server refuses, so the doors stay exactly where they are. */
  onFailed?: () => void;
} = {}) {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const emailId = useId();
  const passwordId = useId();

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    onStarted?.();
    try {
      await signIn(email, password);
      // Nothing else happens here. The auth context flips to signed-in, the
      // dashboard mounts behind the closed doors, and the entrance takes over.
    } catch (err) {
      onFailed?.();
      setError(
        err instanceof ApiRequestError
          ? err.message
          : 'Sign-in could not be completed. Check that the server is running.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="signin" onSubmit={(event) => void onSubmit(event)}>
      <p className="signin__lede">
        Access is scoped to your organisation. Credentials are never stored in this repository.
      </p>

      <div className="signin__field">
        <label className="sr-only" htmlFor={emailId}>
          Email
        </label>
        <span className="signin__icon" aria-hidden="true">
          @
        </span>
        <input
          id={emailId}
          className="signin__input"
          type="email"
          name="email"
          autoComplete="username"
          placeholder="you@newz.nl"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </div>

      <div className="signin__field">
        <label className="sr-only" htmlFor={passwordId}>
          Password
        </label>
        <span className="signin__icon" aria-hidden="true">
          ◈
        </span>
        <input
          id={passwordId}
          className="signin__input"
          type={revealed ? 'text' : 'password'}
          name="password"
          autoComplete="current-password"
          placeholder="Password"
          required
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        <button
          type="button"
          className="signin__reveal"
          onClick={() => setRevealed((current) => !current)}
          aria-pressed={revealed}
          aria-label={revealed ? 'Hide the password' : 'Show the password'}
        >
          <span aria-hidden="true">{revealed ? '◉' : '◎'}</span>
        </button>
      </div>

      {error && (
        <p className="signin__error" role="alert">
          {error}
        </p>
      )}

      <button className="btn btn--primary btn--block signin__submit" type="submit" disabled={busy}>
        <span aria-hidden="true">{busy ? '◌' : '⌾'}</span>
        {busy ? 'Authenticating…' : 'Enter the Bridge'}
      </button>

      <p className="signin__footnote">
        <span aria-hidden="true">▣</span> Session cookies are httpOnly and same-site
      </p>
    </form>
  );
}
